import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Line, Circle as SvgCircle, Text as SvgText, Polyline, Rect, Path } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { FONTS } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import { getStatusColor } from '../../components/charts/chartStyles';
import api from '../../services/api';
import { MainStackParamList } from '../../navigation/MainNavigator';

type ScreenRouteProp = RouteProp<MainStackParamList, 'TalentKPIDetail'>;

interface KPILinkData {
  id: number;
  kpi: number;
  kpi_name: string;
  kpi_weight: number;
  kpi_is_general?: boolean;
  target_value: number;
  achieved_value: number;
  content_type: number;
  object_id: number;
}

interface KPIFullData {
  id: number;
  name: string;
  description: string;
  kpi_type: string;
  polarity: string;
  unit: string;
  frequency: string;
  weight: string;
  formula: string;
  target_value: number;
  owner: string;
  unit_description?: string;
  target_pattern?: string;
  aggregation_method?: string;
  reading_target?: number;
  latest_reading_value?: number;
  base_year?: string;
  baseline?: string;
}

const FOREGROUND = '#414042';

/* ── KPI Trend Chart ──────────────────────────────────────── */
interface TrendChartProps {
  targetValue: number;                          // fallback when a quarter has no grid target
  quarterlyTarget?: (number | null)[];          // [Q1..Q4] period targets
  quarterlyActual: (number | null)[];           // [Q1..Q4] actuals — null = no reading
  baseline: number;
  year: number;
  isRTL: boolean;
}

function KPITrendChart({ targetValue, quarterlyTarget, quarterlyActual, baseline, year, isRTL }: TrendChartProps) {
  const W = 320;
  const H = 230;
  const padL = 30;
  const padR = 8;
  const padT = 12;
  const padB = 32;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const ySteps = 5;
  const labels = [`Q1-${year}`, `Q2-${year}`, `Q3-${year}`, `Q4-${year}`];

  const targets = labels.map((_, i) => quarterlyTarget?.[i] ?? targetValue);

  // Scale to the data instead of a hardcoded 10, rounded up to a clean step so
  // the axis labels stay whole numbers.
  const peak = Math.max(
    baseline,
    ...targets.map(v => v || 0),
    ...quarterlyActual.map(v => v || 0),
  );
  const rawMax = peak > 0 ? peak * 1.15 : 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const yRound = Math.max(ySteps, Math.ceil(rawMax / magnitude) * magnitude);

  const toX = (i: number) => padL + (i / 3) * chartW;
  const toY = (v: number) => padT + chartH - (Math.min(v, yRound) / yRound) * chartH;

  const targetPts = targets.map((v, i) => `${toX(i)},${toY(v || 0)}`).join(' ');
  // Only plot quarters that actually have a reading — a missing quarter used to
  // be drawn as a zero, which read as "performance collapsed".
  const actualPoints = quarterlyActual
    .map((v, i) => ({ v, i }))
    .filter((p): p is { v: number; i: number } => p.v != null);
  const actualPts = actualPoints.map(p => `${toX(p.i)},${toY(p.v)}`).join(' ');

  return (
    <View style={{ alignItems: 'center' }}>
      <Svg width={W} height={H}>
        {/* Left border (Y-axis) */}
        <Line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="#94A3B8" strokeWidth={1} />
        {/* Right border */}
        <Line x1={W - padR} y1={padT} x2={W - padR} y2={padT + chartH} stroke="#E2E8F0" strokeWidth={1} />
        {/* Bottom border (bold) */}
        <Line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#94A3B8" strokeWidth={2} />

        {/* Horizontal grid + Y labels */}
        {Array.from({ length: ySteps + 1 }, (_, i) => {
          const val = Math.round((yRound / ySteps) * i);
          const y = toY(val);
          return (
            <React.Fragment key={i}>
              {i > 0 && <Line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#F1F5F9" strokeWidth={1} />}
              <SvgText x={padL - 6} y={y + 4} fontSize={10} fill="#94A3B8" textAnchor="end" fontFamily="system-ui">{val}</SvgText>
            </React.Fragment>
          );
        })}

        {/* Vertical dashed grid lines at each quarter */}
        {labels.map((_, i) => (
          <Line key={`vg${i}`} x1={toX(i)} y1={padT} x2={toX(i)} y2={padT + chartH} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4,4" />
        ))}

        {/* X labels */}
        {labels.map((label, i) => (
          <SvgText key={i} x={toX(i)} y={H - 6} fontSize={9} fill="#94A3B8" textAnchor="middle" fontFamily="system-ui">{label}</SvgText>
        ))}

        {/* Baseline dashed line */}
        <Line x1={padL} y1={toY(baseline)} x2={W - padR} y2={toY(baseline)} stroke="#94A3B8" strokeWidth={1} strokeDasharray="6,4" />

        {/* Target line (purple) */}
        <Polyline points={targetPts} fill="none" stroke="#7C3AED" strokeWidth={2} />
        {targets.map((v, i) => (
          <SvgCircle key={`t${i}`} cx={toX(i)} cy={toY(v || 0)} r={4} fill="#FFFFFF" stroke="#7C3AED" strokeWidth={2} />
        ))}

        {/* Actual line (teal) */}
        {actualPoints.length > 0 ? (
          <>
            <Polyline points={actualPts} fill="none" stroke="#14B8A6" strokeWidth={2} />
            {actualPoints.map(p => (
              <SvgCircle key={`a${p.i}`} cx={toX(p.i)} cy={toY(p.v)} r={4} fill="#FFFFFF" stroke="#14B8A6" strokeWidth={2} />
            ))}
          </>
        ) : null}
      </Svg>

      {/* Legend */}
      <View style={chartStyles.legend}>
        <View style={chartStyles.legendItem}>
          <View style={[chartStyles.legendDot, { backgroundColor: '#94A3B8' }]} />
          <Text style={chartStyles.legendText}>{isRTL ? 'خط الأساس' : 'Baseline'}</Text>
        </View>
        <View style={chartStyles.legendItem}>
          <View style={[chartStyles.legendDot, { backgroundColor: '#14B8A6' }]} />
          <Text style={chartStyles.legendText}>{isRTL ? 'القيمة الفعلية' : 'Actual'}</Text>
        </View>
        <View style={chartStyles.legendItem}>
          <View style={[chartStyles.legendDot, { backgroundColor: '#7C3AED' }]} />
          <Text style={chartStyles.legendText}>{isRTL ? 'المستهدف' : 'Target'}</Text>
        </View>
      </View>
    </View>
  );
}

const chartStyles = StyleSheet.create({
  legend: { flexDirection: 'row', justifyContent: 'center', gap: 16, paddingTop: 8 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 11, fontFamily: FONTS.regular, color: '#64748B' },
});

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export default function TalentKPIDetailScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { user } = useAuth();
  // Students and guardians are both beneficiaries: same read-only view.
  const { isReadOnlyBeneficiary } = useRoleAccess();
  const readOnly = user?.profile?.user_type === 'strategy_team' || isReadOnlyBeneficiary;
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const kpiLinkId = route.params?.kpiLinkId;

  const [isLoading, setIsLoading] = useState(true);
  const [linkData, setLinkData] = useState<KPILinkData | null>(null);
  const [kpiData, setKpiData] = useState<KPIFullData | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const availableYears = [2026, 2027, 2028, 2029];
  // Readings filter state
  const [readingsYearFilter, setReadingsYearFilter] = useState('all');
  const [readingsStatusFilter, setReadingsStatusFilter] = useState('all');
  const [showReadingsYearDD, setShowReadingsYearDD] = useState(false);
  const [showReadingsStatusDD, setShowReadingsStatusDD] = useState(false);

  // Readings loaded from the API (grouped per measurement date by the backend)
  interface ReadingStudent { student_id: number; student_name: string; value: string; achievement: number; notes: string }
  interface ReadingRow {
    date: string; year: number | null; avg_value: string; target: string;
    achievement: number; participants: number; students: ReadingStudent[];
  }
  const [readings, setReadings] = useState<ReadingRow[]>([]);
  const [readingsIsGeneral, setReadingsIsGeneral] = useState(false);
  const [expandedReading, setExpandedReading] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      const fetchData = async () => {
        if (!kpiLinkId) return;
        setIsLoading(true);
        try {
          const linkResp = await api.get(`/talent-kpi-links/${kpiLinkId}/`);
          const link = linkResp.data;
          setLinkData(link);
          // KPI data is now embedded in the link response
          setKpiData({
            id: link.kpi,
            name: link.kpi_name || '',
            description: link.kpi_description || '',
            kpi_type: link.kpi_type || '',
            polarity: link.kpi_polarity || '',
            unit: link.kpi_unit || '',
            frequency: link.kpi_frequency || '',
            weight: String(link.kpi_weight || 0),
            formula: link.kpi_formula || '',
            target_value: parseFloat(link.kpi_target_value) || 0,
            owner: link.kpi_owner || '',
            baseline: link.kpi_baseline || '',
            base_year: link.kpi_baseline_year ? String(link.kpi_baseline_year) : '',
          });
        } catch (e) {
          console.log('[TalentKPIDetail] Fetch failed:', e);
        }
        try {
          const readingsResp = await api.get(`/talent-kpi-links/${kpiLinkId}/readings/`);
          const rows = readingsResp.data?.results || [];
          setReadings(rows);
          setReadingsIsGeneral(!!readingsResp.data?.is_general);
          // Land the chart on a year that actually has readings, otherwise it
          // opens empty whenever the data predates the current year.
          const years: number[] = rows.map((r: any) => r.year).filter(Boolean);
          if (years.length && !years.includes(chartYear)) {
            setChartYear(Math.max(...years));
          }
        } catch (e) {
          console.log('[TalentKPIDetail] Readings fetch failed:', e);
          setReadings([]);
        }
        setIsLoading(false);
      };
      fetchData();
    }, [kpiLinkId])
  );

  const kpiName = kpiData?.name || linkData?.kpi_name || '';
  const kpiDescription = kpiData?.description || '';

  const polarityLabel = (val?: string) => {
    if (!val) return '-';
    if (val === 'positive' || val === 'ascending' || val === 'increasing') return isRTL ? 'تصاعدي' : 'Ascending';
    if (val === 'negative' || val === 'descending' || val === 'decreasing') return isRTL ? 'تنازلي' : 'Descending';
    return val;
  };

  const kpiTypeLabel = (val?: string) => {
    if (!val) return '-';
    if (val === 'operational') return isRTL ? 'تشغيلي' : 'Operational';
    if (val === 'strategic') return isRTL ? 'استراتيجي' : 'Strategic';
    return val;
  };

  const frequencyLabel = (val?: string) => {
    if (!val) return '-';
    const map: Record<string, string> = {
      monthly: isRTL ? 'شهري' : 'Monthly',
      quarterly: isRTL ? 'ربع سنوي' : 'Quarterly',
      half_yearly: isRTL ? 'نصف سنوي' : 'Semi-Annual',
      yearly: isRTL ? 'سنوي' : 'Annual',
    };
    return map[val] || val;
  };

  const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const isPercentUnit = ['%', 'percentage', 'نسبة'].includes((kpiData?.unit || '').trim().toLowerCase());

  const unitLabel = (val?: string) => {
    if (!val) return '-';
    const map: Record<string, string> = {
      '%': isRTL ? 'نسبة' : 'Percentage',
      'percentage': isRTL ? 'نسبة' : 'Percentage',
      'عدد': isRTL ? 'عدد' : 'Count',
      'number': isRTL ? 'عدد' : 'Number',
      'count': isRTL ? 'عدد' : 'Count',
    };
    return map[val] || val;
  };

  // 2-column layout: right column + left column paired per row (matching Figma)
  type DetailItem = { label: string; value: string; showMore?: boolean; showPercent?: boolean };
  const detailPairs: { right: DetailItem; left: DetailItem }[] = kpiData
    ? [
        { right: { label: isRTL ? 'المسؤول' : 'Owner', value: kpiData.owner || '-' }, left: { label: isRTL ? 'نمط تكرار أو تغير المستهدف' : 'Target Pattern', value: kpiData.target_pattern || (isRTL ? 'ثابت' : 'Fixed') } },
        { right: { label: isRTL ? 'الوزن' : 'Weight', value: kpiData.weight ? `%${kpiData.weight}` : '-' }, left: { label: isRTL ? 'طريقة التجميع السنوي' : 'Aggregation', value: kpiData.aggregation_method || (isRTL ? 'قيمة آخر قراءة' : 'Last reading') } },
        { right: { label: isRTL ? 'النوع' : 'Type', value: kpiTypeLabel(kpiData.kpi_type) }, left: { label: isRTL ? 'المستهدف الكلي' : 'Overall Target', value: kpiData.target_value != null ? String(kpiData.target_value) : '-' } },
        { right: { label: isRTL ? 'دورية القياس' : 'Frequency', value: frequencyLabel(kpiData.frequency) }, left: { label: isRTL ? 'مستهدف القراءة الحالية' : 'Reading Target', value: kpiData.reading_target != null ? String(kpiData.reading_target) : String(kpiData.target_value || '-'), showMore: true } },
        { right: { label: isRTL ? 'وحدة القياس' : 'Unit', value: unitLabel(kpiData.unit) }, left: { label: isRTL ? 'متوسط المحقق الكلي' : 'Overall Achieved', value: linkData?.achieved_value != null ? String(linkData.achieved_value) : '-' } },
        { right: { label: isRTL ? 'وصف وحدة القياس' : 'Unit Desc.', value: kpiData.unit_description || '-' }, left: { label: isRTL ? 'متوسط آخر قراءة' : 'Latest Reading', value: kpiData.latest_reading_value != null ? String(kpiData.latest_reading_value) : '-', showPercent: true } },
        { right: { label: isRTL ? 'اتجاه المؤشر' : 'Polarity', value: polarityLabel(kpiData.polarity) }, left: { label: isRTL ? 'سنة الأساس' : 'Base Year', value: kpiData.base_year || '-' } },
        { right: { label: isRTL ? 'معادلة القياس' : 'Formula', value: kpiData.formula || '-' }, left: { label: isRTL ? 'خط الأساس' : 'Baseline', value: kpiData.baseline || '0' } },
      ]
    : [];

  const readingsHeaders = [
    isRTL ? 'تاريخ القياس' : 'Date',
    isRTL ? 'متوسط القيمة' : 'Avg Value',
    isRTL ? 'المستهدف' : 'Target',
    isRTL ? 'متوسط الإنجاز' : 'Avg Achievement',
    isRTL ? 'عدد المشاركين' : 'Participants',
  ];

  /** Readings of the selected chart year bucketed into Q1..Q4. */
  const chartSeries = useMemo(() => {
    const actualBuckets: number[][] = [[], [], [], []];
    const targets: (number | null)[] = [null, null, null, null];
    readings.forEach(r => {
      if (!r.date || r.year !== chartYear) return;
      const month = new Date(`${r.date}T00:00:00`).getMonth();
      if (Number.isNaN(month)) return;
      const q = Math.floor(month / 3);
      const value = parseFloat(r.avg_value);
      if (!Number.isNaN(value)) actualBuckets[q].push(value);
      const target = parseFloat(r.target);
      if (!Number.isNaN(target)) targets[q] = target;
    });
    const actual = actualBuckets.map(vals =>
      vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
    );
    return { actual, targets };
  }, [readings, chartYear]);

  /** '2026-05-01' → '1 مايو, 2026' / 'May 1, 2026'. */
  const formatReadingDate = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return `${isRTL ? d.getDate() : ''}${isRTL ? ' ' : ''}${(isRTL ? AR_MONTHS : EN_MONTHS)[d.getMonth()]}${isRTL ? '' : ' ' + d.getDate()}, ${d.getFullYear()}`;
  };

  const formatValue = (raw: string) => {
    const num = parseFloat(raw);
    const shown = Number.isNaN(num) ? raw : String(Math.round(num * 100) / 100);
    return isPercentUnit ? `%${shown}` : shown;
  };

  const readingYears = Array.from(
    new Set(readings.map(r => r.year).filter((y): y is number => !!y)),
  ).sort((a, b) => b - a);

  const filteredReadings = readings.filter(r => {
    if (readingsYearFilter !== 'all' && String(r.year) !== readingsYearFilter) return false;
    if (readingsStatusFilter === 'achieved' && r.achievement < 100) return false;
    if (readingsStatusFilter === 'below' && r.achievement >= 100) return false;
    return true;
  });

  const yearFilterLabel = readingsYearFilter === 'all' ? (isRTL ? 'كل السنوات' : 'All Years') : readingsYearFilter;
  const statusFilterLabel = readingsStatusFilter === 'all'
    ? (isRTL ? 'كل الحالات' : 'All Status')
    : readingsStatusFilter === 'achieved'
      ? (isRTL ? 'محقق (100%+)' : 'Achieved (100%+)')
      : (isRTL ? 'دون المستهدف' : 'Below target');

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: isRTL ? 'الرئيسية' : 'Home' },
              { label: isRTL ? 'مبادرة مواهب' : 'Talents Initiative', route: 'Talents' },
              { label: isRTL ? 'تفاصيل مؤشر الأداء' : 'KPI Details' },
            ]}
          />

          {isLoading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator size="large" color={FOREGROUND} />
            </View>
          ) : (
            <View style={styles.sections}>
              {/* 1. Header Card */}
              <View style={[styles.card, createShadow(0, 1, 3, '#000', 0.08)]}>
                <View style={[styles.headerRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <View style={styles.headerText}>
                    <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text style={[styles.kpiName, { textAlign: isRTL ? 'right' : 'left' }]}>{kpiName}</Text>
                      {!!linkData?.kpi_is_general && (
                        <View style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', borderWidth: 1, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 2 }}>
                          <Text style={{ color: '#A9211F', fontSize: 12, fontFamily: FONTS.medium }}>
                            {isRTL ? 'مؤشر عام' : 'Overall KPI'}
                          </Text>
                        </View>
                      )}
                    </View>
                    {!!kpiDescription && (
                      <Text style={[styles.kpiDesc, { textAlign: isRTL ? 'right' : 'left' }]}>{kpiDescription}</Text>
                    )}
                    {!!linkData?.kpi_is_general && (
                      <Text style={[styles.kpiDesc, { textAlign: isRTL ? 'right' : 'left', color: '#94A3B8' }]}>
                        {isRTL ? 'المعدل العام لإنجاز جميع الطلاب' : 'Overall average achievement across all students'}
                      </Text>
                    )}
                  </View>
                  {!readOnly && (
                    <TouchableOpacity
                      style={styles.addReadingBtn}
                      onPress={() => navigation.navigate('AddTalentKPIReading', { kpiLinkId })}
                    >
                      <Ionicons name="add" size={16} color="#fff" />
                      <Text style={styles.addReadingText}>
                        {isRTL ? 'إضافة قراءة جديدة' : 'Add New Reading'}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              {/* 2. Two-column body row */}
              <View style={[styles.bodyRow, isMobile && styles.bodyRowMobile, !isMobile && { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                {/* Right: KPI Details */}
                <View style={[styles.detailsCard, styles.card, createShadow(0, 1, 3, '#000', 0.08), isMobile && styles.fullWidth]}>
                  <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                    {isRTL ? 'تفاصيل المؤشر' : 'KPI Details'}
                  </Text>
                  {detailPairs.map((pair, idx) => {
                    const renderCell = (item: DetailItem) => (
                      <View style={[styles.detailCell, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                        <View style={styles.labelPill}>
                          <Text style={[styles.labelText, { textAlign: isRTL ? 'right' : 'left' }]}>{item.label}</Text>
                        </View>
                        <View style={[styles.valuePill, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <Text style={[styles.valueText, { textAlign: isRTL ? 'right' : 'left' }]}>{item.value}</Text>
                          {item.showPercent && <Text style={styles.percentIcon}>%</Text>}
                          {item.showMore && (
                            <TouchableOpacity style={[styles.moreLink, { flexDirection: isRTL ? 'row-reverse' : 'row' }]} onPress={() => setToastVisible(true)}>
                              <Ionicons name="information-circle-outline" size={14} color="#3B82F6" />
                              <Text style={styles.moreLinkText}>{isRTL ? 'المزيد' : 'More'}</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    );
                    return (
                      <View
                        key={idx}
                        style={[
                          styles.detailRow,
                          { flexDirection: isRTL ? 'row-reverse' : 'row' },
                          idx < detailPairs.length - 1 && styles.detailRowBorder,
                        ]}
                      >
                        {renderCell(pair.right)}
                        {renderCell(pair.left)}
                      </View>
                    );
                  })}
                </View>

                {/* Left: KPI Trend */}
                <View style={[styles.trendCard, styles.card, createShadow(0, 1, 3, '#000', 0.08), isMobile && styles.fullWidth]}>
                  <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>
                    {isRTL ? 'اتجاه المؤشر' : 'KPI Trend'}
                  </Text>
                  {/* Year filter dropdown */}
                  <TouchableOpacity
                    style={[styles.yearFilterDropdown, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                    onPress={() => setShowYearPicker(!showYearPicker)}
                  >
                    <Text style={styles.yearFilterText}>{chartYear}</Text>
                    <Ionicons name="chevron-down" size={14} color="#94A3B8" />
                  </TouchableOpacity>
                  {showYearPicker && (
                    <View style={styles.yearPickerList}>
                      {availableYears.map(yr => (
                        <TouchableOpacity
                          key={yr}
                          style={[styles.yearPickerItem, yr === chartYear && { backgroundColor: '#F1F5F9' }]}
                          onPress={() => { setChartYear(yr); setShowYearPicker(false); }}
                        >
                          <Text style={[styles.yearPickerItemText, yr === chartYear && { color: '#A9211F', fontFamily: FONTS.medium }]}>{yr}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}
                  <KPITrendChart
                    targetValue={kpiData?.reading_target ? Number(kpiData.reading_target) : (kpiData?.target_value ? Number(kpiData.target_value) : 10)}
                    quarterlyTarget={chartSeries.targets}
                    quarterlyActual={chartSeries.actual}
                    baseline={kpiData?.baseline ? Number(kpiData.baseline) : 0}
                    year={chartYear}
                    isRTL={isRTL}
                  />
                </View>
              </View>

              {/* 3. Bottom row */}
              <View style={[styles.bodyRow, isMobile && styles.bodyRowMobile, !isMobile && { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                {/* Right: Readings Log */}
                <View style={[styles.readingsCard, styles.card, createShadow(0, 1, 3, '#000', 0.08), isMobile && styles.fullWidth]}>
                  {/* Header: title + export */}
                  <View style={[styles.readingsHeaderRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left', marginBottom: 0 }]}>
                      {isRTL ? 'سجل القراءات' : 'Readings Log'}
                    </Text>
                    <TouchableOpacity style={styles.exportBtn} onPress={() => setToastVisible(true)}>
                      <Ionicons name="download-outline" size={14} color={FOREGROUND} />
                      <Text style={styles.exportBtnText}>{isRTL ? 'تصدير Excel' : 'Export Excel'}</Text>
                    </TouchableOpacity>
                  </View>
                  {/* Filters */}
                  <View style={[styles.filterRow, { flexDirection: isRTL ? 'row-reverse' : 'row', zIndex: 10 }]}>
                    {/* Year filter */}
                    <View style={{ flex: 1, zIndex: 12 }}>
                      <TouchableOpacity
                        style={[styles.filterDropdown, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                        onPress={() => { setShowReadingsYearDD(!showReadingsYearDD); setShowReadingsStatusDD(false); }}
                      >
                        <Text style={[styles.filterDropdownText, { textAlign: isRTL ? 'right' : 'left' }]}>{yearFilterLabel}</Text>
                        <Ionicons name="chevron-down" size={14} color="#94A3B8" />
                      </TouchableOpacity>
                      {showReadingsYearDD && (
                        <View style={styles.filterDDList}>
                          <TouchableOpacity style={styles.filterDDItem} onPress={() => { setReadingsYearFilter('all'); setShowReadingsYearDD(false); }}>
                            <Text style={styles.filterDDItemText}>{isRTL ? 'كل السنوات' : 'All Years'}</Text>
                          </TouchableOpacity>
                          {(readingYears.length ? readingYears : availableYears).map(yr => (
                            <TouchableOpacity key={yr} style={styles.filterDDItem} onPress={() => { setReadingsYearFilter(String(yr)); setShowReadingsYearDD(false); }}>
                              <Text style={styles.filterDDItemText}>{yr}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                    </View>
                    {/* Status filter */}
                    <View style={{ flex: 1, zIndex: 11 }}>
                      <TouchableOpacity
                        style={[styles.filterDropdown, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                        onPress={() => { setShowReadingsStatusDD(!showReadingsStatusDD); setShowReadingsYearDD(false); }}
                      >
                        <Text style={[styles.filterDropdownText, { textAlign: isRTL ? 'right' : 'left' }]}>{statusFilterLabel}</Text>
                        <Ionicons name="chevron-down" size={14} color="#94A3B8" />
                      </TouchableOpacity>
                      {showReadingsStatusDD && (
                        <View style={styles.filterDDList}>
                          <TouchableOpacity style={styles.filterDDItem} onPress={() => { setReadingsStatusFilter('all'); setShowReadingsStatusDD(false); }}>
                            <Text style={styles.filterDDItemText}>{isRTL ? 'كل الحالات' : 'All Status'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.filterDDItem} onPress={() => { setReadingsStatusFilter('achieved'); setShowReadingsStatusDD(false); }}>
                            <Text style={styles.filterDDItemText}>{isRTL ? 'محقق (100%+)' : 'Achieved (100%+)'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.filterDDItem} onPress={() => { setReadingsStatusFilter('below'); setShowReadingsStatusDD(false); }}>
                            <Text style={styles.filterDDItemText}>{isRTL ? 'دون المستهدف' : 'Below target'}</Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                    <TouchableOpacity style={styles.filterApplyBtn} onPress={() => { setShowReadingsYearDD(false); setShowReadingsStatusDD(false); }}>
                      <Text style={styles.filterApplyText}>{isRTL ? 'تطبيق الفلتر' : 'Apply Filter'}</Text>
                    </TouchableOpacity>
                  </View>
                  {/* Table header */}
                  <View style={[styles.tableHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    {readingsHeaders.map((h, i) => (
                      <Text key={i} style={[styles.tableHeaderCell, { textAlign: isRTL ? 'right' : 'left' }]}>{h}</Text>
                    ))}
                    <View style={{ width: 32 }} />
                  </View>
                  {/* Data rows or empty state */}
                  {filteredReadings.length === 0 ? (
                    <View style={styles.emptyWrap}>
                      <Ionicons name="document-text-outline" size={36} color="#CBD5E1" />
                      <Text style={styles.emptyText}>{isRTL ? 'لا توجد قراءات' : 'No readings'}</Text>
                    </View>
                  ) : (
                    filteredReadings.map((r, idx) => (
                      <React.Fragment key={r.date || idx}>
                      <View style={[styles.readingRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }, idx < filteredReadings.length - 1 && { borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }]}>
                        <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left' }]}>{formatReadingDate(r.date)}</Text>
                        <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left' }]}>{formatValue(r.avg_value)}</Text>
                        <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left' }]}>{formatValue(r.target)}</Text>
                        <View style={[styles.readingCell, { flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 6 }]}>
                          {(() => {
                            const size = 28;
                            const stroke = 5;
                            const radius = (size - stroke) / 2;
                            const circumference = radius * 2 * Math.PI;
                            const pct = Math.min(r.achievement, 100);
                            const offset = circumference - (pct / 100) * circumference;
                            const color = getStatusColor(pct);
                            return (
                              <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                                <SvgCircle cx={size / 2} cy={size / 2} r={radius} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
                                <SvgCircle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={stroke} fill="none" strokeDasharray={`${circumference}`} strokeDashoffset={offset} strokeLinecap="round" rotation={-90} origin={`${size / 2}, ${size / 2}`} />
                              </Svg>
                            );
                          })()}
                          <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND }}>{r.achievement}%</Text>
                        </View>
                        <View style={[styles.readingCell, { flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 6 }]}>
                          <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND }}>{r.participants}</Text>
                          {readingsIsGeneral && r.students.length > 0 && (
                            <TouchableOpacity
                              style={{ width: 32, alignItems: 'center' }}
                              onPress={() => setExpandedReading(expandedReading === r.date ? null : r.date)}
                            >
                              <Ionicons
                                name={expandedReading === r.date ? 'eye-off-outline' : 'eye-outline'}
                                size={16}
                                color="#94A3B8"
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                      {/* Per-student breakdown behind a general KPI's average */}
                      {expandedReading === r.date && r.students.map(s => (
                        <View
                          key={s.student_id}
                          style={[styles.readingRow, {
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            backgroundColor: '#F8FAFC',
                            borderBottomWidth: 1,
                            borderBottomColor: '#F1F5F9',
                          }]}
                        >
                          <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left', color: '#64748B' }]}>
                            {s.student_name || `#${s.student_id}`}
                          </Text>
                          <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left' }]}>{formatValue(s.value)}</Text>
                          <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left' }]}>{formatValue(r.target)}</Text>
                          <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left', color: getStatusColor(Math.min(s.achievement, 100)) }]}>
                            {s.achievement}%
                          </Text>
                          <Text style={[styles.readingCell, { textAlign: isRTL ? 'right' : 'left', color: '#94A3B8' }]} numberOfLines={1}>
                            {s.notes || '—'}
                          </Text>
                        </View>
                      ))}
                      </React.Fragment>
                    ))
                  )}
                  {/* Pagination */}
                  <View style={[styles.paginationRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <Text style={styles.paginationInfo}>
                      {isRTL
                        ? `عرض 1-${filteredReadings.length} من ${filteredReadings.length} عنصر`
                        : `Showing 1-${filteredReadings.length} of ${filteredReadings.length} items`}
                    </Text>
                    <View style={{ flex: 1 }} />
                    <View style={[styles.paginationControls, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <Text style={styles.paginationBtn}>{isRTL ? 'السابق' : 'Previous'}</Text>
                      <View style={styles.paginationPage}><Text style={styles.paginationPageText}>1</Text></View>
                      <Text style={styles.paginationBtn}>{isRTL ? 'التالي' : 'Next'}</Text>
                    </View>
                  </View>
                </View>

                {/* Left: Notes & Comments */}
                <View style={[styles.commentsCard, styles.card, { paddingVertical: 12, paddingHorizontal: 16 }, createShadow(0, 1, 3, '#000', 0.08), isMobile && styles.fullWidth]}>
                  <View style={[styles.commentsHeaderRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left', marginBottom: 0, flex: 1 }]}>
                      {isRTL ? 'الملاحظات والتعليقات' : 'Notes & Comments'}
                    </Text>
                    <View style={styles.commentsIconBtn}>
                      <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
                        <Path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10c0 2.1-.6 4.1-1.7 5.8L21 22l-4.2-.7C15.1 21.4 13.6 22 12 22z" stroke="#414042" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        <Path d="M12 8v8M8 12h8" stroke="#414042" strokeWidth={1.8} strokeLinecap="round" />
                      </Svg>
                    </View>
                  </View>
                  {/* Comments list — grows with content */}
                  <View style={{ marginTop: 12, gap: 10 }}>
                    <View style={[styles.commentBubble, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <View style={[styles.commentAvatar, { backgroundColor: '#A6813F' }]}>
                        <Text style={styles.commentAvatarText}>{isRTL ? 'ف إ' : 'ST'}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <Text style={[styles.commentAuthor, { textAlign: isRTL ? 'right' : 'left' }]}>{isRTL ? 'أحمد محمد' : 'Ahmed M.'}</Text>
                          <Text style={styles.commentDate}>{isRTL ? '١ مايو ٢٠٢٦' : '1 May 2026'}</Text>
                        </View>
                        <Text style={[styles.commentText, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'يرجى مراجعة نسبة الإنجاز للربع الثاني والتأكد من دقة البيانات المدخلة' : 'Please review Q2 achievement percentage and verify data accuracy'}
                        </Text>
                      </View>
                    </View>
                    <View style={[styles.commentBubble, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <View style={[styles.commentAvatar, { backgroundColor: '#9467BD' }]}>
                        <Text style={styles.commentAvatarText}>{isRTL ? 'م ج' : 'OE'}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <Text style={[styles.commentAuthor, { textAlign: isRTL ? 'right' : 'left' }]}>{isRTL ? 'سارة علي' : 'Sara A.'}</Text>
                          <Text style={styles.commentDate}>{isRTL ? '٣ مايو ٢٠٢٦' : '3 May 2026'}</Text>
                        </View>
                        <Text style={[styles.commentText, { textAlign: isRTL ? 'right' : 'left' }]}>
                          {isRTL ? 'تم التحقق من البيانات وتحديث القراءات' : 'Data verified and readings updated'}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
      <Toast visible={toastVisible} message={isRTL ? 'قريباً' : 'Coming soon'} onHide={() => setToastVisible(false)} />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1 },
  loadingWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 80 },
  sections: { gap: 20 },
  card: { backgroundColor: '#FFFFFF', borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', padding: 16 },
  headerRow: { alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  headerText: { flex: 1, gap: 6 },
  kpiName: { fontSize: 18, fontFamily: FONTS.bold, color: FOREGROUND, lineHeight: 26 },
  kpiDesc: { fontSize: 14, fontFamily: FONTS.regular, color: '#64748B', lineHeight: 20 },
  addReadingBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#A9211F', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8 },
  addReadingText: { fontSize: 13, fontFamily: FONTS.medium, color: '#FFFFFF' },
  bodyRow: { flexDirection: 'row' as const, gap: 20 },
  bodyRowMobile: { flexDirection: 'column' },
  fullWidth: { flex: undefined, width: '100%' as any },
  detailsCard: { flex: 3 },
  sectionTitle: { fontSize: 15, fontFamily: FONTS.bold, color: FOREGROUND, marginBottom: 12, lineHeight: 22 },
  detailRow: { alignItems: 'stretch', paddingVertical: 6, gap: 20 },
  detailCell: { flex: 1, alignItems: 'center', gap: 8 },
  detailRowBorder: { borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  labelPill: { backgroundColor: 'rgba(51,65,85,0.1)', borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, minWidth: 140, justifyContent: 'center' },
  labelText: { fontSize: 12, fontFamily: FONTS.regular, color: '#334155', lineHeight: 18, flexWrap: 'wrap' },
  valuePill: { flex: 1, backgroundColor: 'rgba(51,65,85,0.04)', borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  valueText: { flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: FOREGROUND, lineHeight: 18 },
  percentIcon: { fontSize: 12, fontFamily: FONTS.medium, color: '#64748B' },
  moreLink: { alignItems: 'center', gap: 4 },
  moreLinkText: { fontSize: 12, fontFamily: FONTS.regular, color: '#3B82F6' },
  trendCard: { flex: 2 },
  yearFilterDropdown: { alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, marginBottom: 12 },
  yearFilterText: { fontSize: 14, fontFamily: FONTS.regular, color: FOREGROUND },
  yearPickerList: { borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  yearPickerItem: { paddingHorizontal: 14, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#E2E8F0' },
  yearPickerItemText: { fontSize: 14, fontFamily: FONTS.regular, color: FOREGROUND, textAlign: 'right' },
  placeholderWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 60, gap: 12 },
  placeholderText: { fontSize: 14, fontFamily: FONTS.regular, color: '#94A3B8' },
  readingsCard: { flex: 3 },
  readingsHeaderRow: { alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  exportBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 6, paddingHorizontal: 12, paddingVertical: 6 },
  exportBtnText: { fontSize: 13, fontFamily: FONTS.regular, color: FOREGROUND },
  filterRow: { alignItems: 'center', gap: 8, marginBottom: 12 },
  filterDropdown: { alignItems: 'center', gap: 6, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 8 },
  filterDropdownText: { flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: '#94A3B8' },
  filterDDList: { position: 'absolute', top: 40, left: 0, right: 0, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 6, zIndex: 20, elevation: 10 },
  filterDDItem: { paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#F1F5F9' },
  filterDDItemText: { fontSize: 13, fontFamily: FONTS.regular, color: FOREGROUND, textAlign: 'right' },
  filterApplyBtn: { backgroundColor: '#F1F5F9', borderRadius: 6, paddingHorizontal: 14, paddingVertical: 8 },
  filterApplyText: { fontSize: 13, fontFamily: FONTS.medium, color: FOREGROUND },
  readingRow: { alignItems: 'center', paddingVertical: 10, gap: 4 },
  readingCell: { flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: FOREGROUND },
  achievementDot: { width: 10, height: 10, borderRadius: 5 },
  tableHeader: { borderBottomWidth: 1, borderBottomColor: '#E2E8F0', paddingBottom: 8, marginBottom: 8, gap: 4 },
  tableHeaderCell: { flex: 1, fontSize: 12, fontFamily: FONTS.medium, color: '#64748B', lineHeight: 16 },
  emptyWrap: { justifyContent: 'center', alignItems: 'center', paddingVertical: 40, gap: 8 },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular, color: '#94A3B8' },
  paginationRow: { alignItems: 'center', paddingTop: 12, borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  paginationInfo: { fontSize: 12, fontFamily: FONTS.regular, color: '#94A3B8' },
  paginationControls: { alignItems: 'center', gap: 8 },
  paginationBtn: { fontSize: 12, fontFamily: FONTS.regular, color: '#94A3B8' },
  paginationPage: { width: 28, height: 28, borderRadius: 4, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center' },
  paginationPageText: { fontSize: 12, fontFamily: FONTS.medium, color: FOREGROUND },
  commentsCard: { flex: 2, alignSelf: 'flex-start' },
  commentsHeaderRow: { alignItems: 'center', justifyContent: 'space-between' },
  commentsIconBtn: { width: 32, height: 32, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center', justifyContent: 'center' },
  commentBubble: { gap: 10, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#E2E8F0' },
  commentAvatar: { width: 32, height: 32, borderRadius: 4, alignItems: 'center', justifyContent: 'center' },
  commentAvatarText: { fontSize: 10, fontFamily: FONTS.medium, color: '#FFFFFF' },
  commentAuthor: { fontSize: 13, fontFamily: FONTS.medium, color: FOREGROUND },
  commentDate: { fontSize: 11, fontFamily: FONTS.regular, color: '#94A3B8' },
  commentText: { fontSize: 13, fontFamily: FONTS.regular, color: '#64748B', lineHeight: 20 },
});
